import React, { useRef, useState } from "react";
import { toPng } from "html-to-image";
import "./MyJourneyPanel.css";

/**
 * MyJourneyPanel — proud "look what I've built on Emergent" rundown.
 * Triggered by voice ("tell me my journey") or by the JOURNEY button in
 * the Quick Actions FAB. Counts ARE NOT made up — every value comes from
 * the backend `my_journey` action result (real DB counts + live env scan).
 *
 * "Share as image" exports the panel as a high-DPI PNG and either uses
 * the native Web Share API (mobile / supported browsers) or triggers a
 * download fallback. The PNG is screenshot-ready for social/Slack.
 */
export default function MyJourneyPanel({ data }) {
  const d = data || {};
  const captureRef = useRef(null);
  const [shareState, setShareState] = useState("idle"); // idle | rendering | shared | error
  const tiles = [
    { k: "APPS BUILT",        v: fmt(d.apps_built),          sub: `+${d.registered_external_apps || 0} registered`, accent: "gold" },
    { k: "CONVERSATIONS",     v: fmt(d.conversations_total), sub: "with your agents",                              accent: "mint" },
    { k: "API KEYS WIRED",    v: fmt(d.api_keys_total),      sub: `${d.services_armed || 0} / ${d.services_total || 0} services armed`, accent: "magenta" },
    { k: "WEBHOOKS",          v: fmt(d.webhooks_total),      sub: "real-time triggers",                            accent: "cyan" },
    { k: "VOICE ACTIONS",     v: fmt(d.voice_actions_available), sub: "natural-language commands",                accent: "yellow" },
    { k: "BACKEND LOC",       v: fmt(d.backend_lines_of_code), sub: "Python · async",                              accent: "purple" },
    { k: "HOURS OF GRAFT",    v: fmt(d.hours_estimate),      sub: "conservative estimate",                         accent: "gold-bright" },
    { k: "SPEND",             v: `£${fmt(d.spend_gbp)}`,     sub: "infra · tokens",                                accent: "coral" },
  ];

  const handleShare = async () => {
    if (!captureRef.current) return;
    setShareState("rendering");
    try {
      // High-DPI render (2x) so social platforms don't pixelate
      const dataUrl = await toPng(captureRef.current, {
        pixelRatio: 2,
        backgroundColor: "#000",
        cacheBust: true,
        // Skip remote font stylesheets — they trigger CORS warnings on
        // cssRules access without affecting the rendered PNG (fonts fall
        // back to the locally-loaded faces). Cleaner console.
        filter: (node) => !(
          node.tagName === "LINK" &&
          typeof node.href === "string" &&
          /fonts\.googleapis|fonts\.gstatic/.test(node.href)
        ),
      });
      const fileName = `raw-company-journey-${new Date().toISOString().slice(0, 10)}.png`;
      // Convert to Blob → File for the Web Share API
      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], fileName, { type: "image/png" });
      const shareText = `My RAW-DATA rundown — ${fmt(d.apps_built)} apps · ${fmt(d.api_keys_total)} keys · ${fmt(d.hours_estimate)} hours of graft.`;
      const canFileShare = typeof navigator !== "undefined" && typeof navigator.canShare === "function" && navigator.canShare({ files: [file] });
      if (canFileShare && typeof navigator.share === "function") {
        await navigator.share({ files: [file], title: "RAW-DATA · Journey", text: shareText });
        setShareState("shared");
      } else {
        // Fallback: download the PNG
        const a = document.createElement("a");
        a.href = dataUrl;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setShareState("shared");
      }
      setTimeout(() => setShareState("idle"), 2500);
    } catch (e) {
      console.warn("[journey-share] failed:", e?.message);
      setShareState("error");
      setTimeout(() => setShareState("idle"), 2500);
    }
  };

  return (
    <div className="journey-panel" data-testid="my-journey-panel" ref={captureRef}>
      <div className="journey-header">
        <div className="journey-eyebrow">// PROUD MOMENT · YOUR EMERGENT JOURNEY</div>
        <div className="journey-title-row">
          <div className="journey-title">EVERYTHING YOU&apos;VE PUT IN</div>
          <button
            type="button"
            className="journey-share-btn"
            onClick={handleShare}
            disabled={shareState === "rendering"}
            data-testid="journey-share-btn"
            title="Export as PNG (share or save)"
          >
            {shareState === "rendering" && "EXPORTING…"}
            {shareState === "idle" && "↗ SHARE AS IMAGE"}
            {shareState === "shared" && "✓ DONE"}
            {shareState === "error" && "✗ FAILED"}
          </button>
        </div>
        <div className="journey-sub">A receipt of the graft.</div>
      </div>

      <div className="journey-grid">
        {tiles.map((t) => (
          <div key={t.k} className={`journey-tile accent-${t.accent}`} data-testid={`journey-tile-${t.k.toLowerCase().replace(/\s+/g, '-')}`}>
            <div className="journey-tile-label">{t.k}</div>
            <div className="journey-tile-value">{t.v}</div>
            <div className="journey-tile-sub">{t.sub}</div>
          </div>
        ))}
      </div>

      {Array.isArray(d.highlights) && d.highlights.length > 0 && (
        <div className="journey-highlights">
          <div className="journey-highlights-label">HIGHLIGHTS</div>
          <ul>
            {d.highlights.map((h, i) => (
              <li key={i}><span className="bullet">▸</span>{h}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="journey-footer">
        Built with RAW-DATA · {new Date().getFullYear()}
      </div>
    </div>
  );
}

function fmt(n) {
  const v = Number(n || 0);
  if (!Number.isFinite(v)) return "0";
  return v.toLocaleString();
}
